import { Component, computed, input, output } from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { type ButtonColor, type ButtonSize, type ButtonVariant } from './button.model';

@Component({
  selector: 'lib-button',
  imports: [IconComponent],
  styleUrls: [
    '../../tokens/colors.css',
    '../../tokens/btn-tokens.css',
    './button.component.css',
  ],
  templateUrl: './button.component.html',
  host: {
    'role': 'button',
    '[attr.tabindex]': 'isInactive() ? -1 : 0',
    '[attr.aria-disabled]': 'isInactive()',
    '[attr.aria-busy]': 'loading()',
    '[attr.aria-label]': 'label() || null',
    '[attr.title]': 'title() || null',
    '[attr.type]': 'buttonType()',
    '[class]': 'hostClasses()',
    '[style.pointer-events]': 'isInactive() ? "none" : null',
    '(click)': 'onHostClick($event)',
    '(keydown.enter)': 'onKeyPress($event)',
    '(keydown.space)': 'onKeyPress($event)',
  },
})
export class ButtonComponent {
  label = input<string>();
  icon = input<string>();
  iconSvg = input<string>();
  iconPosition = input<'left' | 'right'>('left');
  variant = input<ButtonVariant>('solid');
  size = input<ButtonSize>('md');
  color = input<ButtonColor>('primary');
  dropdown = input<boolean>(false);
  fullWidth = input<boolean>(false);
  disabled = input<boolean>(false);
  /** Shows a spinner and blocks clicks while an async action triggered by this button is in flight. */
  loading = input<boolean>(false);
  title = input<string>();
  buttonType = input<'button' | 'submit' | 'reset'>('button');

  clicked = output<void>();

  readonly isInactive = computed(() => this.disabled() || this.loading());

  readonly hostClasses = computed(() => {
    const variant = this.variant();
    const size = this.size();
    const color = this.color();

    return {
      'msa-btn': true,
      [`msa-btn--${size}`]: true,
      [`msa-btn--${color}`]: true,
      [`msa-btn--${variant}`]: true,
      'msa-btn--full-width': this.fullWidth(),
      'msa-btn--disabled': this.disabled(),
      'msa-btn--loading': this.loading(),
    };
  });

  onHostClick(_event: Event): void {
    if (!this.isInactive()) {
      this.clicked.emit();
    }
  }

  onKeyPress(event: Event): void {
    if (!this.isInactive()) {
      event.preventDefault();
      this.clicked.emit();
    }
  }
}
